import { useState, useMemo, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import shortid from 'shortid';
import { Box, Button } from '@rsos/base-components';
import { selectExistingRequest } from '@rsos/reduxDataStore';
import {
  resetSelectedAirAmbulance,
  setFormPageToShow,
} from '@rsos/reduxDataStore/src/features/airAmbulance/airAmbulanceSlice';
import { viewAllCalls } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import {
  AIR_AMBULANCE_CARD,
  setCardsToRender,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import { trackEditAirAmbulanceRequest } from '../../../gainsightTrackings/airAmbulanceTrackings';
import { Loader } from '../AirAmbulanceForm/AirAmbulanceFormStandalone.styles';
import { FORM_PAGES } from '../AirAmbulanceForm/constants';
import { LAUNCH_TYPES } from '../AirAmbulanceForm/constants';
import { useFormSubmit, useTentativeETA } from '../AirAmbulanceForm/hooks';
import { checkLaunchType } from '../AirAmbulanceForm/utils';
import { formatCoordinates } from '../AirAmbulanceForm/validation';
import AirAmbulanceControlsModal from './AirAmbulanceModal';

const AirAmbulanceStatusControls = () => {
  const [isLoading, setIsLoading] = useState(false);
  const existingRequest = useSelector(selectExistingRequest);
  const tentativeETA = useTentativeETA();
  const existingRequestBody = existingRequest?.body;
  const isSecondaryLaunchType = checkLaunchType(
    existingRequestBody?.trip_data?.launch_type,
  );

  // The UX could be improved by surfacing to the user that we don't know the
  // current status of the request.
  const isCancelled = Boolean(
    existingRequestBody?.status?.find(
      row => row?.type === 'cancelPending' || row?.type === 'cancelled',
    ),
  );

  const [showModal, setShowModal] = useState(false);
  const dispatch = useDispatch();

  // Track previous state to detect when Standby button should disappear
  const prevIsStandby = useRef(isSecondaryLaunchType === LAUNCH_TYPES.STANDBY);

  // Reset loading state when Standby button disappears
  useEffect(() => {
    const currentIsStandby = isSecondaryLaunchType === LAUNCH_TYPES.STANDBY;

    // If it WAS standby but is no longer standby, reset loading
    if (prevIsStandby.current && !currentIsStandby && isLoading) {
      setIsLoading(false);
    }

    // Update the ref for next comparison
    prevIsStandby.current = currentIsStandby;
  }, [isSecondaryLaunchType, isLoading]);

  const toggleModal = (bool = null) => {
    setShowModal(bool || !showModal);
  };

  const handleEditRequest = () => {
    // Track the edit button click
    trackEditAirAmbulanceRequest({
      requestId:
        existingRequestBody?.ext_trip_id ||
        existingRequestBody?.id ||
        existingRequest?.request_id,
      requestType: existingRequestBody?.trip_data?.launch_type,
    });

    dispatch(setFormPageToShow(FORM_PAGES.FORM));
  };

  const handleCancel = () => {
    toggleModal(false);
  };

  const handleCreateNewRequest = async () => {
    await dispatch(viewAllCalls());
    await dispatch(resetSelectedAirAmbulance());
    dispatch(setCardsToRender(AIR_AMBULANCE_CARD));
  };

  const UUID = useMemo(() => shortid.generate(), []);
  const handleSubmit = useFormSubmit(
    isLoading,
    setIsLoading,
    tentativeETA,
    UUID,
    false,
    true,
  );
  const handleActivateRequest = () => {
    // Construct form values from existing request data
    // Based on the actual data structure, coordinates are at existingRequestBody.location
    let formattedCoordinates = '';

    if (
      existingRequestBody?.location?.latitude &&
      existingRequestBody?.location?.longitude
    ) {
      const latitude = parseFloat(existingRequestBody.location.latitude);
      const longitude = parseFloat(existingRequestBody.location.longitude);

      if (!isNaN(latitude) && !isNaN(longitude)) {
        formattedCoordinates = formatCoordinates(latitude, longitude);
      }
    }

    const formValues = {
      pickup_coordinates: formattedCoordinates,
      pickup_location: existingRequestBody?.trip_data?.pickup_location || '',
      pickup_details: existingRequestBody?.trip_data?.pickup_details || '',
      request_reason: existingRequestBody?.trip_data?.trip_reason
        ? {
            value: existingRequestBody.trip_data.trip_reason,
            label: existingRequestBody.trip_data.trip_reason,
          }
        : 'Select',
      ground_contact: existingRequestBody?.trip_data?.ground_contact || '',
      patient_info: existingRequestBody?.trip_data?.patient_info || '',
      air_service_declined: existingRequestBody?.trip_data?.weather_decline
        ? 'Yes'
        : 'No',
      air_service_responding: existingRequestBody?.trip_data?.another_carrier
        ? 'Yes'
        : 'No',
      multiple_units_requested: existingRequestBody?.trip_data
        ?.multiple_units_requested
        ? 'Yes'
        : 'No',
      launch_type: existingRequestBody?.trip_data?.launch_type || 'production',
    };

    handleSubmit(formValues);
  };

  return (
    <>
      <Box horizontal justify="space-between" mt={2} mb={2}>
        {isCancelled ? (
          <Button
            btnType="tertiary"
            type="button"
            grow
            onClick={handleCreateNewRequest}
          >
            Create New Air Ambulance Request
          </Button>
        ) : (
          <>
            <Button
              type="button"
              grow
              btnType="warning_tertiary"
              onClick={toggleModal}
              disabled={isLoading}
            >
              Cancel{isSecondaryLaunchType && ` ${isSecondaryLaunchType}`}
            </Button>
            <Button
              grow
              btnType="tertiary"
              type="button"
              onClick={handleEditRequest}
              disabled={isLoading}
            >
              Edit{isSecondaryLaunchType && ` ${isSecondaryLaunchType}`}
            </Button>
            {isSecondaryLaunchType === LAUNCH_TYPES.STANDBY && (
              <Button
                grow
                btnType="primary"
                type="button"
                onClick={handleActivateRequest}
                disabled={isLoading}
              >
                {isLoading ? <Loader /> : 'Activate Standby'}
              </Button>
            )}
          </>
        )}
      </Box>
      <AirAmbulanceControlsModal
        showModal={showModal}
        onClose={() => toggleModal(false)}
        handleCancel={handleCancel}
      />
    </>
  );
};

export default AirAmbulanceStatusControls;
